import type { SignupTimingBucket, DemoAttendeeRow, LeadCalRow } from "./sql";
import type { Filters } from "../../lib/funnel/filters";
import type { CountryGroup } from "../../lib/funnel/helpers";
import { groupCountry } from "../../lib/funnel/helpers";

export const SIGNUP_TIMING_LABELS: Record<SignupTimingBucket, string> = {
  before: "Signed up before demo",
  on_day: "Signed up on demo day",
  within_7d: "Signed up within 7 days",
  within_30d: "Signed up within 30 days",
  later: "Signed up later",
  never: "Never signed up",
};

export const SIGNUP_TIMING_ORDER: SignupTimingBucket[] = [
  "before",
  "on_day",
  "within_7d",
  "within_30d",
  "later",
  "never",
];

// Country fallback chain: matched-user country → booking country (when paired
// with a lead_cal row) → email TLD heuristic → "Rest of World".
export function groupCountryFallback(row: DemoAttendeeRow): CountryGroup {
  if (row.user_country_code) return groupCountry(row.user_country_code);
  if (row.booking_country_code) return groupCountry(row.booking_country_code);
  return countryFromEmail(row.attendee_email);
}

function countryFromEmail(email: string): CountryGroup {
  const at = email.indexOf("@");
  if (at < 0) return "Rest of World";
  const tld = email.slice(at + 1).split(".").pop()?.toLowerCase() ?? "";
  if (tld === "nz") return "NZ";
  if (tld === "au") return "AU";
  if (tld === "uk") return "GB";
  if (tld === "us") return "US";
  return "Rest of World";
}

// Cohort + country gate only. Used by the headline "Demos run" KPI so it
// counts every demo in the date+country window, even ones whose attendees are
// all role-filtered out. Without this split, toggle ON drops 5 demos in
// Mar 2026 where every attendee is support-role and the headline drifts away
// from the spine.
export function applyDemoCohortFilters(rows: DemoAttendeeRow[], f: Filters): DemoAttendeeRow[] {
  const monthSet = new Set(f.months);
  const countrySet = new Set(f.countries);
  const demoTypeSet = new Set(f.demoTypes ?? []);
  const demoerSet = new Set(f.demoers ?? []);
  const dayMode = f.granularity === "day" && (!!f.cohortDayFrom || !!f.cohortDayTo);
  const dayFrom = f.cohortDayFrom ?? null;
  const dayTo = f.cohortDayTo ?? f.cohortDayFrom ?? null;
  return rows.filter((r) => {
    const cohortDate = r.demo_date_utc;
    if (dayMode) {
      const d = cohortDate.slice(0, 10);
      if (dayFrom && d < dayFrom) return false;
      if (dayTo && d > dayTo) return false;
    } else if (monthSet.size > 0) {
      if (!monthSet.has(cohortDate.slice(0, 7))) return false;
    }
    if (countrySet.size > 0) {
      const grp = groupCountryFallback(r);
      if (!countrySet.has(grp)) return false;
    }
    // Demo type lives at cohort level: it's about what counts as a demo, not
    // who attended. Null demo_type (pre-Feb-2026 backfill) passes through so
    // the historical cohort isn't silently dropped.
    if (demoTypeSet.size > 0 && r.demo_type != null && !demoTypeSet.has(r.demo_type)) return false;
    if (demoerSet.size > 0 && !demoerSet.has(r.organiser_email ?? "(unknown)")) return false;
    return true;
  });
}

// Full filter chain: cohort + country + demo type + role + support toggle.
export function applyDemoFilters(rows: DemoAttendeeRow[], f: Filters): DemoAttendeeRow[] {
  const roleSet = new Set(f.roles);
  return applyDemoCohortFilters(rows, f).filter((r) => {
    // Role pick is an explicit "show only these roles" — rows without role
    // data (unmatched attendees, role-less matched users) cannot satisfy it
    // and must be dropped. Previously these escaped via the `r.user_role` null
    // guard, which let Role=support + toggle ON leak 186 role-null rows.
    if (roleSet.size > 0 && (r.user_role == null || !roleSet.has(r.user_role))) return false;
    if (f.excludeSupport && r.user_role === "support") return false;
    return true;
  });
}

export function applyLeadCalFilters(rows: LeadCalRow[], f: Filters): LeadCalRow[] {
  const monthSet = new Set(f.months);
  const dayMode = f.granularity === "day" && (!!f.cohortDayFrom || !!f.cohortDayTo);
  const dayFrom = f.cohortDayFrom ?? null;
  const dayTo = f.cohortDayTo ?? f.cohortDayFrom ?? null;
  return rows.filter((r) => {
    const cohortDate = r.booked_at;
    if (dayMode) {
      const d = cohortDate.slice(0, 10);
      if (dayFrom && d < dayFrom) return false;
      if (dayTo && d > dayTo) return false;
    } else if (monthSet.size > 0) {
      if (!monthSet.has(cohortDate.slice(0, 7))) return false;
    }
    return true;
  });
}

// Demo-level rollup: collapse attendee rows to one row per demo (any attendee
// row will do — they all share demo metadata; we just take the first).
export function uniqueDemos(rows: DemoAttendeeRow[]): DemoAttendeeRow[] {
  const seen = new Map<string, DemoAttendeeRow>();
  for (const r of rows) {
    if (!seen.has(r.demo_external_id)) seen.set(r.demo_external_id, r);
  }
  return Array.from(seen.values());
}

export function uniqueAttendees(rows: DemoAttendeeRow[]): Set<string> {
  const out = new Set<string>();
  for (const r of rows) out.add(r.attendee_email);
  return out;
}

export type ViewMode = "attendee" | "company";

// Stable per-row key for company-level collapse: matched company_external_id
// when known, otherwise the demo itself. A company that ran multiple demos
// but never signed up therefore counts as one prospect per demo (acknowledged
// in info copy); once any attendee signs up, all rows tied to that company
// collapse into a single prospect across demos.
export function companyKey(r: DemoAttendeeRow): string {
  return r.company_external_id ?? `demo:${r.demo_external_id}`;
}

export function companyDisplayName(r: DemoAttendeeRow): string {
  return (
    r.company_name_from_user ??
    r.company_name_native ??
    r.company_name_from_context ??
    (r.organiser_email ? `Demo with ${r.organiser_email}` : "Unmatched prospect")
  );
}

function flattenJsonish(v: unknown): unknown[] {
  if (v == null || v === "") return [];
  if (Array.isArray(v)) return v.flatMap(flattenJsonish);
  if (typeof v === "string") {
    const trimmed = v.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith("[")) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return flattenJsonish(parsed);
      } catch {
        // fall through
      }
    }
    return [trimmed];
  }
  return [v];
}

function unionJsonArrays(values: unknown[]): unknown {
  const seen = new Set<string>();
  const out: unknown[] = [];
  for (const v of values) {
    for (const item of flattenJsonish(v)) {
      const norm = typeof item === "string" ? item.trim().toLowerCase() : JSON.stringify(item);
      if (!norm || seen.has(norm)) continue;
      seen.add(norm);
      out.push(item);
    }
  }
  return out.length > 0 ? out : null;
}

// Collapse attendee rows to one row per company (or per unmatched demo).
// First-touch attribution: representative metadata (demo date, organiser, demo
// type, etc.) comes from the earliest demo in the group; outcome flags are OR-d
// across attendees; jsonb arrays are unioned across the group's demos.
export function collapseToCompany(rows: DemoAttendeeRow[]): DemoAttendeeRow[] {
  const groups = new Map<string, DemoAttendeeRow[]>();
  for (const r of rows) {
    const k = companyKey(r);
    const arr = groups.get(k);
    if (arr) arr.push(r);
    else groups.set(k, [r]);
  }

  const out: DemoAttendeeRow[] = [];
  for (const group of groups.values()) {
    const sortedByDate = [...group].sort((a, b) => a.demo_date_utc.localeCompare(b.demo_date_utc));
    const earliestDemoId = sortedByDate[0]!.demo_external_id;
    const earliestDemoRows = sortedByDate.filter((r) => r.demo_external_id === earliestDemoId);
    // Prefer a matched attendee from the earliest demo as the representative
    // (so user_full_name / user_role / user_country_code carry meaningful data).
    const rep =
      earliestDemoRows.find((r) => r.user_external_id != null) ??
      sortedByDate.find((r) => r.user_external_id != null) ??
      sortedByDate[0]!;

    const anySignedUp = group.some((r) => r.signup_timing_bucket !== "never");
    const anyActivated = group.some((r) => r.activated);
    const anyPaid = group.some((r) => r.on_paid_plan === true);
    const billingPlan = group.find((r) => r.on_paid_plan === true)?.billing_plan ?? rep.billing_plan;

    // Best signup timing bucket: pick row with min days_demo_to_signup ≥ 0;
    // fall back to any non-never bucket; otherwise 'never'.
    let bestTimingRow: DemoAttendeeRow | null = null;
    for (const r of group) {
      if (r.signup_timing_bucket === "never") continue;
      if (!bestTimingRow) { bestTimingRow = r; continue; }
      const a = r.days_demo_to_signup;
      const b = bestTimingRow.days_demo_to_signup;
      if (a != null && (b == null || a < b)) bestTimingRow = r;
    }

    const lastDemoDate = sortedByDate[sortedByDate.length - 1]!.demo_date_utc;

    out.push({
      ...rep,
      // Outcome rollups
      signup_timing_bucket: bestTimingRow?.signup_timing_bucket ?? "never",
      days_demo_to_signup: bestTimingRow?.days_demo_to_signup ?? null,
      activated: anyActivated,
      on_paid_plan: anyPaid ? true : (rep.on_paid_plan ?? null),
      billing_plan: anyPaid ? billingPlan : rep.billing_plan,
      // Demo identity stays first-touch; expose last_demo via demo_date_utc?
      // Keep demo_date_utc = earliest, callers that need spread use group counts.
      demo_date_utc: rep.demo_date_utc,
      // Union jsonb arrays across the group's demos (one entry per demo).
      competitors_mentioned: unionJsonArrays(sortedByDate.map((r) => r.competitors_mentioned)),
      integration_requests: unionJsonArrays(sortedByDate.map((r) => r.integration_requests)),
      security_concerns: unionJsonArrays(sortedByDate.map((r) => r.security_concerns)),
      pain_points: unionJsonArrays(sortedByDate.map((r) => r.pain_points)),
      pricing_signals: unionJsonArrays(sortedByDate.map((r) => r.pricing_signals)),
      value_of_marloo: unionJsonArrays(sortedByDate.map((r) => r.value_of_marloo)),
      objections: unionJsonArrays(sortedByDate.map((r) => r.objections)),
      transcript_signals: unionJsonArrays(sortedByDate.map((r) => r.transcript_signals)),
      // Hint that there may be later demos; not exposed in current panels but
      // available for tooltips.
      first_demo_date: rep.first_demo_date ?? lastDemoDate,
    });
  }
  return out;
}
